import { graphqlClient } from '@/lib/graphql-client';
import { buildPagingLiteral, buildWhereLiteral } from '@/lib/graphql-query-builder';
import { pickScopeStamp, SCOPE_FIELDS, ScopeField, ScopeStamp } from '@/lib/scope-stamp';

const buildGetUserScopeQuery = (userId: string) => `
  query {
    UserProfileDetails: getUserProfileDetails(
      where: ${buildWhereLiteral({ UserId: userId })}
      order: []
      paging: ${buildPagingLiteral(1, 1)}
    ) {
      items {
        ${SCOPE_FIELDS.join('\n        ')}
      }
    }
  }
`;

/**
 * A user's department as insert-ready scope fields, read from their UserProfileDetail
 * (by its `UserId`, the IAM user id). This is the one place scope is looked up: every record that
 * carries `SCOPE_FIELDS` is stamped from it at insert time (see `withUserScope`), so the stamp is
 * always the profile's current value, never a stale or not-yet-loaded cached one.
 */
export const getUserScope = async (userId: string): Promise<ScopeStamp> => {
  if (!userId) return {};
  const result = await graphqlClient.query<{ UserProfileDetails: { items: ScopeStamp[] } }>({
    query: buildGetUserScopeQuery(userId),
  });
  return pickScopeStamp(result.UserProfileDetails.items[0]);
};

/**
 * `input` stamped with the scope of `userId`, the employee the new record is about. Pass
 * `inherited` for a record that belongs to an existing one (a correction of an attendance day, a
 * payoff of an overtime balance): the parent's own stamp is kept field by field, and the profile
 * only fills what the parent is missing.
 */
export const withUserScope = async <T extends object>(
  userId: string,
  input: T,
  inherited?: Partial<Record<ScopeField, string | null | undefined>>
): Promise<T & ScopeStamp> => ({
  ...input,
  ...(await getUserScope(userId)),
  ...pickScopeStamp(inherited),
});
